import { apiFetch } from "../lib/api";

export type AccountRole = "owner" | "manager";

export interface AccountProfile {
  name: string;
  email: string;
  role: AccountRole;
}

export interface AccountOption {
  id: string;
  name: string;
  role: AccountRole;
  active: boolean;
}

export interface AccountDomain {
  domain: string;
  status:
    | "active"
    | "submitting"
    | "failed"
    | "review_required"
    | "connected";
  ownership: "registered" | "external";
  expiresAt?: string;
  attachedProject: { id: string; name: string } | null;
}

export interface AccountPerson {
  id: string;
  email: string;
  role: "owner" | "manager";
  status: "active" | "pending";
  expiresAt?: string;
}

export class AccountApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = "AccountApiError";
  }
}

export async function fetchAccount(): Promise<AccountProfile> {
  return (await accountRequest<{ account: AccountProfile }>("/account"))
    .account;
}

export async function fetchAccounts(): Promise<AccountOption[]> {
  return (await accountRequest<{ accounts: AccountOption[] }>("/account/accounts"))
    .accounts;
}

export async function switchAccount(accountId: string): Promise<AccountProfile> {
  return (
    await accountRequest<{ account: AccountProfile }>("/account/switch", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ accountId }),
    })
  ).account;
}

export async function updateAccountName(name: string): Promise<AccountProfile> {
  return (
    await accountRequest<{ account: AccountProfile }>("/account", {
      method: "PATCH",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ name }),
    })
  ).account;
}

export async function fetchAccountDomains(): Promise<AccountDomain[]> {
  return (
    await accountRequest<{ domains: AccountDomain[] }>("/account/domains")
  ).domains;
}

export async function fetchAccountPeople(): Promise<AccountPerson[]> {
  return (await accountRequest<{ people: AccountPerson[] }>("/account/members"))
    .people;
}

export async function inviteManager(email: string): Promise<AccountPerson> {
  return (
    await accountRequest<{ person: AccountPerson }>("/account/invitations", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ email }),
    })
  ).person;
}

export async function revokeManagerInvitation(invitationId: string): Promise<void> {
  await accountRequest(`/account/invitations/${encodeURIComponent(invitationId)}`, {
    method: "DELETE",
  });
}

export async function removeManager(memberId: string): Promise<void> {
  await accountRequest(`/account/members/${encodeURIComponent(memberId)}`, {
    method: "DELETE",
  });
}

async function accountRequest<T>(
  path: string,
  init: RequestInit = {},
): Promise<T> {
  const response = await apiFetch(path, { ...init, credentials: "include" });
  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as
      | { error?: string }
      | null;
    throw new AccountApiError(
      response.status,
      accountErrorMessage(body?.error, response.status),
    );
  }
  return (await response.json()) as T;
}

function accountErrorMessage(error: string | undefined, status: number): string {
  if (error === "account_ambiguous") {
    return "Choose an account by signing in again.";
  }
  if (error === "forbidden") return "Only the account owner can do that.";
  if (error === "invalid_account") return "Enter an account name.";
  if (error === "invalid_account_selection") return "Choose a valid account.";
  if (error === "invalid_invitation") return "Enter a valid email address.";
  if (error === "cannot_invite_self") return "You already own this account.";
  if (error === "member_exists") return "That person already has access.";
  if (error === "account_auth_unavailable") {
    return "Sign in again before inviting someone.";
  }
  if (error === "invitation_failed") return "The invitation could not be sent.";
  if (error === "invitation_revoke_failed") {
    return "The invitation could not be revoked.";
  }
  if (error === "member_not_found") return "That manager no longer has access.";
  return error ?? `Account request failed (${status})`;
}
